<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="500"
  >
    <template v-slot:activator="{ on }">
      <v-btn
        color="hrissecondary"
        class="white--text px-10"
        block
        v-on="on"
      >
        Create
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="hrissecondary"
      title="Cutoff Types"
      text="Create cutoff type"
      class="mb-0"
      :loading="loading"
    > 
      <form v-if="dialog && !loading">
        <v-container>
          <v-row no-gutters>
            <v-col
              cols="12"
              class="pb-0"
            >
              <v-text-field
                v-model="code"
                v-validate="'required'" 
                label="Code"
                :error-messages="errors.collect('code')"
                data-vv-name="code"    
                data-vv-as="code" 
                outlined
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0"
            >
              <v-text-field
                v-model="name"
                v-validate="'required'" 
                label="Name"
                :error-messages="errors.collect('name')"
                data-vv-name="name"    
                data-vv-as="name" 
                outlined
              />
            </v-col>

            <v-col
              cols="12"
              class="mb-4"
            >
              <tiptap-vuetify
                v-model="description"
                class="full-width text-editor-toolbar-height pb-1"
                placeholder="Description"
                :extensions="extensions"
              />
            </v-col>

            <v-col
              cols="12"
              class="pb-0"
            >
              <v-autocomplete
                v-model="type" 
                v-validate="'required'"
                :error-messages="errors.collect('type')"    
                data-vv-name="type" 
                data-vv-as="cutoff type"
                label="Cutoff Type"
                item-text="name"
                item-value="name"
                outlined
                :items="types"
                @change="type === 'monthly' ? endDay = 31 : endDay = 7"
              />
            </v-col>

            <v-row
              v-if="type"
              no-gutters
            >
              <v-col
                cols="12"
                class="pb-0 pr-2"
                sm="12"
                md="9"
                lg="9"  
                xl="9"
              >
                <v-autocomplete
                  v-model="startPeriod" 
                  v-validate="'required'"
                  :error-messages="errors.collect('startPeriod')"    
                  data-vv-name="startPeriod" 
                  data-vv-as="start period"
                  label="Start Period"
                  item-text="name"
                  item-value="name"
                  :items="type === 'monthly' ? monthPeriods : weekPeriods"
                  outlined
                />
              </v-col>

              <v-col
                cols="12"
                class="pb-0"
                sm="12"
                md="3"
                lg="3"  
                xl="3"
              >
                <v-text-field
                  v-model="startDay" 
                  :rules="[rules.startDayRequired,rules.min,rules.max]"
                  label="Start Day"
                  type="number"
                  outlined
                  @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                />
              </v-col>

              <v-col
                cols="12"
                class="pb-0 pr-2"
                sm="12"
                md="9"
                lg="9"  
                xl="9"
              >
                <v-autocomplete
                  v-model="endPeriod" 
                  v-validate="'required'"
                  :error-messages="errors.collect('endPeriod')"    
                  data-vv-name="endPeriod" 
                  data-vv-as="end period"
                  label="End Period"
                  item-text="name"
                  item-value="name"
                  :items="type === 'monthly' ? monthPeriods : weekPeriods"
                  outlined
                />
              </v-col>

              <v-col
                cols="12"
                class="pb-0"
                sm="12"
                md="3"
                lg="3"  
                xl="3"
              >
                <v-text-field
                  v-model="endDay" 
                  :rules="[rules.endDayRequired,rules.min,rules.max]"
                  label="End Day"
                  type="number"
                  outlined
                  @keydown.native="['e', 'E', '+', '-', '.'].includes($event.key) && $event.preventDefault()"
                />
              </v-col>
            </v-row>
          </v-row>
        </v-container>
      </form> 
      
      <v-card-actions v-if="dialog && !loading">
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="hrissecondary" 
          class="white--text px-10"         
          @click="submitForm"
        >
          Save
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  import { TiptapVuetify, Heading, Bold, Italic, Underline, BulletList, OrderedList, ListItem, Link, HardBreak } from 'tiptap-vuetify'

  export default {
    $_veeValidate: {
      validator: 'new', 
    },
    components: {
      TiptapVuetify,
    },
    data () {
      return {
        loading: false,
        dialog: false,        
        progressing: false,    
        code: null,   
        description: null,
        type: null,
        startPeriod: null,
        endPeriod: null,
        startDay: 1,
        endDay: 31,
        name: null,
        types: [
          { name: 'monthly' }, 
          { name: 'weekly' },
        ],
        monthPeriods: [
          { name: 'current_month' }, 
          { name: 'previous_month' },
        ],
        weekPeriods: [
          { name: 'current_week' }, 
          { name: 'previous_week' },
        ],
        rules: {
          startDayRequired: value => !!value || 'The start day field is required',     
          endDayRequired: value => !!value || 'The end day field is required',     
          min: value => value >= 1 || 'Minimum number is 1',      
          max: value => this.type === 'monthly' ? (value <= 31 || 'Maximum number is 31') : (value <= 7 || 'Maximum number is 7'),      
        },  
        extensions: [
          Link,
          Underline,
          Italic,
          ListItem,
          BulletList,
          OrderedList,
          [Heading, {
            options: {
              levels: [1, 2, 3],
            },
          }],
          Bold,
          HardBreak,
        ],
      }
    },
    watch: {
      dialog (value) {
        if (!value) return this.$resetData()
        
        this.loading = true
        this.loading = false
      },
    },
    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return 

            this.progressing = true

            this.$api.cutoffTypes().create(this.prepareFormData())
              .then((res) => {
                if (res.status === 201) {      
                  this.$emit('created')                
                  this.progressing = false
                  this.dialog = false
                }
              })
              .catch(() => {
                this.progressing = false
              })
          })
      },   
   
      prepareFormData () {
        return {
          code: this.code,
          name: this.name,
          description: this.description,
          type: this.type,
          start_period: this.startPeriod,
          start_day: this.startDay,
          end_period: this.endPeriod,
          end_day: this.endDay,
        }
      },
    },
  }
</script>

<style scoped>
.text-editor-toolbar-height>>> .v-toolbar__content{
  height: unset !important;
  min-height: 42px;
}
.text-editor-toolbar-height>>>.v-sheet.v-sheet--tile.theme--light.v-toolbar.v-toolbar--dense.v-toolbar--flat.grey.lighten-4{
  min-height:unset !important;
}
.full-width{
  width: 100%;
}
</style>
